import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { AlertTriangle, Eye, CheckCircle, XCircle, Clock, Flag, X, ShieldAlert, FileText, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const Reports = () => {
  const { colors, isDark } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const reportsQuery = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(reportsQuery);
      
      const reportsData = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      // Enrich with property and reporter info
      const enriched = await Promise.all(
        reportsData.map(async (report) => {
          let propertyTitle = null;
          let propertyId = report.propertyId || null;
          let reportedByName = null;

          if (propertyId) {
            try {
              const propSnap = await getDoc(doc(db, 'properties', propertyId));
              if (propSnap.exists()) {
                propertyTitle = propSnap.data().title || null;
              }
            } catch (_) {}
          }

          if (report.reportedBy) {
            try {
              const userSnap = await getDoc(doc(db, 'users', report.reportedBy));
              if (userSnap.exists()) {
                const ud = userSnap.data();
                reportedByName = ud.displayName || ud.email || null;
              }
            } catch (_) {}
          }

          return { ...report, propertyTitle, propertyId, reportedByName };
        })
      );

      setReports(enriched);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId, status) => {
    try {
      const reportRef = doc(db, 'reports', reportId);
      await updateDoc(reportRef, {
        status,
        adminNotes: adminNotes || null,
        resolvedAt: status === 'resolved' ? new Date() : null,
        updatedAt: new Date()
      });

      setReports(reports.map(report =>
        report.id === reportId
          ? { ...report, status, adminNotes, resolvedAt: status === 'resolved' ? new Date() : null }
          : report
      ));

      toast.success(`Report ${status} successfully`);
      setSelectedReport(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, border: `${colors.warning}30`, icon: Clock, text: 'Pending' },
      investigating: { bg: colors.primaryGhost, color: colors.primary, border: `${colors.primary}30`, icon: Eye, text: 'Investigating' },
      resolved: { bg: colors.successGhost, color: colors.success, border: `${colors.success}30`, icon: CheckCircle, text: 'Resolved' },
      rejected: { bg: colors.errorGhost, color: colors.error, border: `${colors.error}30`, icon: XCircle, text: 'Rejected' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span 
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase border"
        style={{ backgroundColor: badge.bg, color: badge.color, borderColor: badge.border }}
      >
        <Icon className="w-3 h-3 mr-1.5" />
        {badge.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { bg: colors.errorGhost, color: colors.error, border: `${colors.error}30` },
      medium: { bg: colors.warningGhost, color: colors.warning, border: `${colors.warning}30` },
      low: { bg: colors.successGhost, color: colors.success, border: `${colors.success}30` }
    };
    const badge = badges[priority] || badges.medium;
    return (
      <span 
        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black tracking-widest uppercase border"
        style={{ backgroundColor: badge.bg, color: badge.color, borderColor: badge.border }}
      >
        {priority || 'Medium'}
      </span>
    );
  };

  const filteredReports = filterStatus === 'all' ? reports : reports.filter(report => report.status === filterStatus);

  const stats = {
    pending: reports.filter(r => r.status === 'pending').length,
    investigating: reports.filter(r => r.status === 'investigating').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
    highPriority: reports.filter(r => r.priority === 'high' && r.status !== 'resolved' && r.status !== 'rejected').length
  };

  const kpis = [
    { id: 'pending', label: 'Pending Review', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'investigating', label: 'Investigating', value: stats.investigating, icon: Eye, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'resolved', label: 'Resolved Tickets', value: stats.resolved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'highPriority', label: 'High Priority (Unresolved)', value: stats.highPriority, icon: AlertTriangle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)', isAlert: true },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Safety & Moderation
          </h1>
          <p className="mt-1 text-sm font-medium" style={{ color: colors.textSecondary }}>
            Review flagged content, user disputes, and system reports.
          </p>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const isActiveFilter = filterStatus === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 transition-all cursor-pointer opacity-0 animate-fadeInScale stagger-${i+1} ${isActiveFilter ? 'ring-2 ring-offset-2 dark:ring-offset-gray-900' : 'hover:-translate-y-1 hover:shadow-lg'}`}
              style={{
                backgroundColor: isActiveFilter ? (isDark ? `${kpi.color}15` : `${kpi.color}10`) : colors.card,
                border: `1px solid ${isActiveFilter ? kpi.color : colors.border}`,
                ringColor: kpi.color
              }}
              onClick={() => setFilterStatus(isActiveFilter ? 'all' : kpi.id)}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div 
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${kpi.isAlert && kpi.value > 0 ? 'animate-pulse' : ''}`}
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-3xl font-black" style={{ color: kpi.isAlert && kpi.value > 0 ? colors.error : colors.text }}>
                    {kpi.value.toLocaleString()}
                  </p>
                </div>
                <p className="text-[11px] font-extrabold uppercase tracking-wider" style={{ color: isActiveFilter ? kpi.color : colors.textSecondary }}>
                  {kpi.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Active Filter Pill */}
      {filterStatus !== 'all' && (
        <div className="flex items-center gap-2">
           <span className="text-[12px] font-bold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Active Filter:</span>
           <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-500 border border-blue-500/20 flex items-center gap-1.5 cursor-pointer hover:bg-blue-500/20 transition-colors" onClick={() => setFilterStatus('all')}>
              {filterStatus} <X className="w-3 h-3" />
           </span>
        </div>
      )}

      {/* Reports Table */}
      <div className="card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
             <div className="flex flex-col items-center justify-center p-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
                <p className="mt-4 text-[14px] font-medium" style={{ color: colors.textSecondary }}>Loading reports...</p>
             </div>
        ) : filteredReports.length === 0 ? (
             <div className="flex flex-col items-center justify-center p-16">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
                  <Flag className="w-8 h-8" style={{ color: colors.textMuted }} />
               </div>
               <h3 className="text-[15px] font-bold" style={{ color: colors.text }}>No reports found</h3>
               <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
                 {reports.length === 0 ? "You're all caught up! No reports submitted." : `No ${filterStatus} reports currently match.`}
               </p>
             </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Details</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Property</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Category</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Priority</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Date Logged</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: colors.border }}>
                {filteredReports.map((report) => (
                  <tr 
                    key={report.id} 
                    className="hover:bg-opacity-50 transition-colors"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                         <div className="w-8 h-8 rounded bg-gray-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FileText className="w-4 h-4 text-gray-400" />
                         </div>
                         <div>
                            <div className="text-[14px] font-bold" style={{ color: colors.text }}>
                              {report.title || report.reason || 'Untitled Report'}
                            </div>
                            <div className="text-[12px] truncate max-w-[200px] mt-0.5" style={{ color: colors.textSecondary }}>
                              {report.description || report.details || 'No description provided'}
                            </div>
                            {report.reportedByName && (
                              <div className="text-[11px] mt-0.5 font-medium" style={{ color: colors.textMuted }}>By: {report.reportedByName}</div>
                            )}
                         </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {report.propertyTitle ? (
                        <div>
                          <div className="text-[13px] font-bold truncate max-w-[180px]" style={{ color: colors.text }}>{report.propertyTitle}</div>
                          <div className="text-[11px] font-mono mt-0.5" style={{ color: colors.textMuted }}>ID: {report.propertyId?.slice(0, 10)}...</div>
                        </div>
                      ) : (
                        <span className="text-[12px]" style={{ color: colors.textMuted }}>—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-[12px] font-bold capitalize px-2 py-1 rounded bg-gray-500/5 border border-gray-500/10" style={{ color: colors.text }}>
                        {report.type || 'general'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(report.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-[12px] font-bold" style={{ color: colors.textSecondary }}>
                        {report.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="px-4 py-2 rounded-xl text-[12px] font-bold transition-all shadow-sm flex items-center gap-2 ml-auto hover:-translate-y-0.5"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary, border: `1px solid ${colors.primary}30` }}
                      >
                        Inspect <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ticket Resolution Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div className="rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl relative animate-fadeInScale" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            
            {/* Header */}
            <div className="px-8 py-6 border-b flex items-start justify-between relative" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
               {selectedReport.priority === 'high' && <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>}
               <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner" style={{ backgroundColor: colors.surfaceHover, border: `1px solid ${colors.border}` }}>
                     <ShieldAlert className="w-6 h-6" style={{ color: colors.textMuted }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-black tracking-tight" style={{ color: colors.text }}>Incident Ticket</h2>
                    <p className="text-[12px] font-bold uppercase tracking-widest mt-1 opacity-70" style={{ color: colors.textSecondary }}>ID: <span className="font-mono">{selectedReport.id.slice(0, 10)}...</span></p>
                  </div>
               </div>
               <button onClick={() => { setSelectedReport(null); setAdminNotes(''); }} className="p-2 rounded-full hover:rotate-90 transition-all duration-300 bg-white/5" style={{ backgroundColor: colors.background }}>
                 <X className="w-5 h-5" style={{ color: colors.text }} />
               </button>
            </div>

            {/* Body */}
            <div className="p-8 flex-1 overflow-y-auto space-y-6">
                
                <div className="grid grid-cols-2 gap-4">
                   <div className="p-4 rounded-2xl border" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: colors.textSecondary }}>Report Title</p>
                      <p className="text-[14px] font-bold" style={{ color: colors.text }}>{selectedReport.title || selectedReport.reason || 'Untitled'}</p>
                   </div>
                   <div className="p-4 rounded-2xl border" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                      <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: colors.textSecondary }}>Classification</p>
                      <div className="flex items-center gap-3">
                         <span className="text-[13px] font-black uppercase text-gray-500">{selectedReport.type || 'General'}</span>
                         {getPriorityBadge(selectedReport.priority)}
                      </div>
                   </div>
                </div>

                <div className="p-5 rounded-2xl border" style={{ backgroundColor: colors.backgroundSecondary, borderColor: colors.border }}>
                   <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: colors.textSecondary }}>Detailed Description</p>
                   <p className="text-[14px] leading-relaxed" style={{ color: colors.text }}>
                     {selectedReport.description || 'No additional details provided by the reporter.'}
                   </p>
                </div>

                <div className="flex items-center gap-3">
                   <span className="text-[12px] font-bold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Current State:</span>
                   {getStatusBadge(selectedReport.status)}
                </div>

                {/* Property Information */}
                {selectedReport.propertyId && (
                  <div className="p-4 rounded-2xl border" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: colors.textSecondary }}>Reported Property</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Title</p>
                        <p className="text-[14px] font-bold" style={{ color: colors.text }}>{selectedReport.propertyTitle || 'Unknown Property'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Property ID</p>
                        <p className="text-[12px] font-mono font-bold" style={{ color: colors.textSecondary }}>{selectedReport.propertyId}</p>
                      </div>
                    </div>
                    {selectedReport.reportedByName && (
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>Reported By</p>
                        <p className="text-[13px] font-bold" style={{ color: colors.text }}>{selectedReport.reportedByName}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedReport.status !== 'resolved' && selectedReport.status !== 'rejected' && (
                  <div className="space-y-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                    <div>
                      <label className="block text-[11px] font-black uppercase tracking-widest mb-2" style={{ color: colors.textSecondary }}>Admin Resolution Log</label>
                      <textarea
                        className="w-full p-4 rounded-xl text-[13px] focus:outline-none transition-all shadow-inner font-medium resize-none"
                        style={{ backgroundColor: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                        rows={4}
                        placeholder="Detail the steps taken or reason for dismissal..."
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
                        onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      {selectedReport.status === 'pending' && (
                        <button
                          onClick={() => updateReportStatus(selectedReport.id, 'investigating')}
                          className="col-span-3 lg:col-span-1 py-3 rounded-xl font-bold text-[12px] text-white shadow-md hover:-translate-y-0.5 transition-all text-center"
                          style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}
                        >
                          Start Investigation
                        </button>
                      )}
                      
                      <button
                        onClick={() => updateReportStatus(selectedReport.id, 'resolved')}
                        className={`py-3 rounded-xl font-bold text-[12px] text-white shadow-md hover:-translate-y-0.5 transition-all text-center flex items-center justify-center gap-2 ${selectedReport.status === 'pending' ? 'col-span-3 lg:col-span-1' : 'col-span-3 lg:col-span-2'}`}
                        style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                      >
                         <CheckCircle className="w-4 h-4"/> Mark Resolved
                      </button>
                      
                      <button
                        onClick={() => updateReportStatus(selectedReport.id, 'rejected')}
                        className="col-span-3 lg:col-span-1 py-3 rounded-xl font-bold text-[12px] text-white shadow-md hover:-translate-y-0.5 transition-all text-center flex items-center justify-center gap-2"
                        style={{ background: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)' }}
                      >
                         <XCircle className="w-4 h-4"/> Reject Report
                      </button>
                    </div>
                  </div>
                 )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;